import {useMemo, useState} from "react";

import {getEffectiveColumnsPerPage} from "./utils.ts";

/**
 * A React hook to paginate columns, preserving the first (sticky) column.
 * Returns the currently visible slice of columns and their original indexes.
 *
 * If `columnsPerPage` is null or <= 1, pagination is disabled and the full
 * column set is returned with no internal calculations.
 *
 * @param columns - The full list of columns, where the first is sticky
 * @param columnsPerPage - The total number of columns per page (including sticky)
 * @param width - The width of the container (required for responsive columns)
 */
export function useColumnPagination<T>(
    columns: T[],
    columnsPerPage: number | "responsive" | null,
    width: number,
) {
    const [currentPage, setCurrentPage] = useState(0);

    // Dynamically determine how many columns to show per page
    // unless a number is passed in.
    const effectiveColumnsPerPage = useMemo(() => {
        if (columnsPerPage === "responsive") {
            return getEffectiveColumnsPerPage(width);
        }

        if (typeof columnsPerPage === "number") {
            return columnsPerPage;
        }

        return undefined;
    }, [width, columnsPerPage]);

    // Column pagination is considered disabled if no value or only sticky column would be shown
    const paginationDisabled =
        effectiveColumnsPerPage == null || effectiveColumnsPerPage <= 1;

    // Always treat the first column as sticky
    const stickyColumn = columns[0];

    // Remaining columns are considered for pagination
    const nonStickyColumns = columns.slice(1);

    // The number of non-sticky columns shown per page
    const pageSize =
        effectiveColumnsPerPage != null
            ? effectiveColumnsPerPage - 1
            : nonStickyColumns.length;

    // Calculate total number of paginated column pages
    const totalPages = useMemo(() => {
        return paginationDisabled
            ? 1
            : Math.ceil(nonStickyColumns.length / pageSize);
    }, [paginationDisabled, nonStickyColumns.length, pageSize]);

    // Compute the start and end indexes for the current paginated column window
    const paginatedColumnStartIndex = paginationDisabled
        ? 0
        : currentPage * pageSize;
    const paginatedColumnEndIndex = paginationDisabled
        ? nonStickyColumns.length
        : paginatedColumnStartIndex + pageSize;

    // Select the current visible columns (non-sticky), then reattach sticky column at the start
    const slicedColumns = nonStickyColumns.slice(
        paginatedColumnStartIndex,
        paginatedColumnEndIndex,
    );

    const visibleColumns = useMemo(
        () => [stickyColumn, ...slicedColumns],
        [stickyColumn, slicedColumns],
    );

    // Map the visible columns to their original indexes in the full column list
    const columnIndexes = useMemo(
        () => [
            0, // sticky column always index 0
            ...slicedColumns.map(
                (_, i) => paginatedColumnStartIndex + 1 + i, // +1 accounts for sticky column
            ),
        ],
        [slicedColumns, paginatedColumnStartIndex],
    );

    // Boundaries for column pagination navigation
    const hasNextPaginatedColPage =
        !paginationDisabled && currentPage < totalPages - 1;
    const hasPreviousPaginatedColPage = !paginationDisabled && currentPage > 0;

    return {
        visibleColumns, // Columns currently rendered in the table
        columnIndexes, // Their corresponding original indexes from the full column array
        currentPage, // Current page of paginated columns
        totalPages, // Total number of column pages
        hasNextPaginatedColPage, // Whether a next column page exists
        hasPreviousPaginatedColPage, // Whether a previous column page exists
        goToNextPaginatedColPage: () => {
            if (!paginationDisabled) {
                setCurrentPage((p) => Math.min(p + 1, totalPages - 1));
            }
        },
        goToPreviousPaginatedColPage: () => {
            if (!paginationDisabled) {
                setCurrentPage((p) => Math.max(p - 1, 0));
            }
        },
        effectiveColumnsPerPage, // The number of columns to show per page
    };
}
